import { Component, Inject, OnInit } from '@angular/core';
import { ExportService } from '../../services/export.service';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { IProjection } from '../../interfaces/mapSettings.interface';
import { MapService } from '../../services/map.service';
import { I18NService } from '@ebw-monorepo/i18n-translate';
import { MAT_DIALOG_DATA } from '@angular/material/dialog';
import { IMapTableData } from '../../interfaces/mapTable.interface';
import { MapTableService } from '@ebw-monorepo/map';
import { transform } from 'ol/proj';

/**
 * Component for displaying an export dialog
 */
@Component({
  selector: 'ebw-monorepo-async-export-dialog',
  templateUrl: './export-dialog.component.html',
  styleUrls: ['./export-dialog.component.scss']
})
export class ExportDialogComponent implements OnInit {
  public form: FormGroup;
  public registeredProjections: IProjection[] = [];
  public availableTypes: string[] = [];
  public availableFormats: string[] = ['PDF', 'CSV', 'Excel', 'Shape', 'HTML'];

  constructor(
    @Inject(MAT_DIALOG_DATA)
    public data: { syncData: IMapTableData; asyncData: { queryId: number; queryName: string } },
    private _exportService: ExportService,
    private _mapService: MapService,
    private _i18nService: I18NService,
    private _mapTableService: MapTableService
  ) {}

  ngOnInit(): void {
    this.form = new FormGroup({
      format: new FormControl('', [Validators.required]),
      projection: new FormControl('', [Validators.required])
    });

    if (this.data.syncData) {
      this.availableTypes.push('table');
    }

    if (this.data.asyncData) {
      this.availableTypes.push('full');
    }

    if (this.availableTypes.length > 1) {
      this.form.addControl('type', new FormControl('', [Validators.required]));
    }

    this.registeredProjections = this._mapService.registeredProjections;
  }

  /**
   * Method called when pressing the export button
   *
   * @author Joshua Micheletti <j.micheletti@ebw.it>
   */
  public onExport(): void {
    // check if the form is invalid, if it is, mark all the controls as touched. that way they show up as red with an error
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    // extract the state of the form
    const formValues: { format: 'csv' | 'pdf' | 'excel' | 'shape' | 'html'; projection: string; type?: string } =
      this.form.getRawValue();

    // if there's no value on the type, set it depending on the available data
    if (!formValues.type) {
      formValues.type = this.data.asyncData ? 'full' : 'table';
    }

    // export the data sync or async depending on how the type was set
    if (formValues.type === 'full') {
      this._exportService.exportAsync(
        formValues.format,
        this.data.asyncData.queryId,
        this.data.asyncData.queryName,
        formValues.projection
      );
    } else {
      let formattedData;

      // format the data accordingly depending on the export type
      switch (formValues.format.toLowerCase()) {
        case 'excel':
          formattedData = this._mapTableService.formatExportData(
            this.data.syncData,
            false,
            true,
            formValues.projection
          );
          break;
        case 'csv':
          formattedData = this._mapTableService.formatExportData(
            this.data.syncData,
            false,
            false,
            formValues.projection
          );
          break;
        case 'pdf':
          formattedData = this._mapTableService.formatExportData(
            this.data.syncData,
            true,
            false,
            formValues.projection
          );
          break;
        case 'html':
          formattedData = this._mapTableService.formatExportData(
            this.data.syncData,
            true,
            false,
            formValues.projection
          );
          break;
        case 'shape':
          formattedData = [];
          for (const feature of this.data.syncData.data) {
            formattedData.push(this.reprojectGeoJSONFeature(feature, 'EPSG:3857', formValues.projection));
          }
          break;
        default:
          formattedData = this.data.syncData;
          break;
      }

      // export the data
      this._exportService.export(
        formValues.format,
        formattedData,
        this.data.syncData.name ?? 'export',
        {},
        formValues.projection
      );
    }
  }
  
  public getTooltipInfo(projection: IProjection): string {
    let info = '';

    if (!projection) {
      return info;
    }

    if (!projection.info) {
      return info;
    }

    if (projection.info.datum) {
      info += `${this._i18nService.getInstantValue('map.sidebar.shape.tooltips.datum')}: ${projection.info.datum}`;
    }

    if (projection.info.projection) {
      if (info.length !== 0) {
        info += '\n';
      }

      info += `${this._i18nService.getInstantValue('map.sidebar.shape.tooltips.projection')}: ${
        projection.info.projection
      }`;
    }

    if (projection.info.zone) {
      if (info.length !== 0) {
        info += '\n';
      }

      info += `${this._i18nService.getInstantValue('map.sidebar.shape.tooltips.zone')}: ${projection.info.zone}`;
    }

    return info;
  }

  public getSelectedTooltipInfo(code: string): string {
    const info = '';

    const foundElement = this.registeredProjections.find((el) => {
      return el.code === code;
    });

    if (!foundElement) {
      return info;
    }

    return this.getTooltipInfo(foundElement);
  }

  public getDisplayName(projection: IProjection): string {
    let name = '';

    name += projection.code;

    if (projection.info) {
      if (projection.info.name) {
        name += ` ${projection.info.name}`;
      }
    }

    return name;
  }

  reprojectGeoJSONFeature(feature: any, sourceProj: string, targetProj: string): any {
    if (!feature.geometry || feature.type !== "Feature") {
      return feature;
    }
  
    const { type, coordinates } = feature.geometry;
  
    function reprojectCoordinates(coords: any): any {
      if (Array.isArray(coords[0])) {
        return coords.map(reprojectCoordinates); // Handle nested arrays
      } else {
        return transform(coords, sourceProj, targetProj);
      }
    }
  
    feature.geometry.coordinates = reprojectCoordinates(coordinates);
    return feature;
  }
}
